import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { REPORTS_REFRESH_INTERVAL_MS } from "./api";
import { TReportFilters } from "./types";

/**
 * Shared data hook for report pages: fetches whenever `filters` change
 * (URL-driven), silently re-fetches every `refreshIntervalMs` (live data), and
 * exposes a manual refresh.
 *
 * The next refetch is scheduled off the moment the previous one *finished*,
 * rather than on a fixed interval running since mount. Two reasons:
 *  - the countdown in the header can only be truthful if the timer restarts
 *    when a fetch completes (a manual refresh would otherwise desync it);
 *  - a fetch that outlives its interval can no longer have polls stack up
 *    behind it, since the next one is only scheduled once it settles.
 */
export function useReportData<T>(
  fetcher: (filters: TReportFilters) => Promise<T>,
  filters: TReportFilters,
  enabled: boolean = true,
  /** Override for pages whose payload is too large to poll at the default rate. */
  refreshIntervalMs: number = REPORTS_REFRESH_INTERVAL_MS
) {
  const { t } = useTranslation();

  const [data, setData] = useState<T>();
  /** A blocking load: first paint, a filter change, or the refresh button. */
  const [isLoading, setIsLoading] = useState<boolean>(true);
  /**
   * A background poll is in flight. Kept separate from `isLoading` so pages can
   * SHOW that a refresh is happening without blanking the data that is already
   * on screen — the skeleton/overlay treatment would blink the whole page on
   * every tick.
   */
  const [isRefetching, setIsRefetching] = useState<boolean>(false);
  /** Last successful fetch — drives the freshness dot. */
  const [lastUpdated, setLastUpdated] = useState<Date>();
  /**
   * Last fetch attempt, successful or not — drives scheduling and the
   * countdown. Tracked separately so a failed poll still reschedules the next
   * one; keying the timer off `lastUpdated` alone would stop polling forever
   * after a single failure.
   */
  const [lastAttempt, setLastAttempt] = useState<Date>();

  const fetchData = useCallback(
    async (silent: boolean = false) => {
      if (silent) setIsRefetching(true);
      else setIsLoading(true);
      try {
        const result = await fetcher(filters);
        setData(result);
        setLastUpdated(new Date());
      } catch {
        if (!silent) toast.error(t("errorInGettingData"));
      } finally {
        setLastAttempt(new Date());
        if (silent) setIsRefetching(false);
        else setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetcher, filters]
  );

  useEffect(() => {
    if (enabled) fetchData();
  }, [fetchData, enabled]);

  // live data: silent background refresh, one hop at a time
  useEffect(() => {
    if (!enabled || refreshIntervalMs <= 0 || !lastAttempt) return;
    const id = setTimeout(() => fetchData(true), refreshIntervalMs);
    return () => clearTimeout(id);
  }, [enabled, refreshIntervalMs, lastAttempt, fetchData]);

  return {
    data,
    isLoading,
    isRefetching,
    lastUpdated,
    lastAttempt,
    refreshIntervalMs,
    refresh: () => fetchData(false),
  };
}

export default useReportData;
